import { useMemo } from 'react';
import { useAuth, Workspace } from '../contexts/AuthContext';

/**
 * Organization role constants (matching backend OrgRoles enum)
 * @see FlashbackClient/src/api/types/platform/roles.ts
 */
export const OrgRoles = {
  USER: 0x00,            // Default role - basic user
  BILLING: 0x01,         // Can manage billing and subscriptions
  WORKSPACES: 0x02,      // Can manage workspaces and team members
  ADMINISTRATORS: 0xfe,  // Administrative access
  OWNER: 0xff,           // Full organization access
} as const;

export type OrgRole = typeof OrgRoles[keyof typeof OrgRoles];

/**
 * Workspace role types (as returned by API)
 */
export type WorkspaceRole = 'ADMIN' | 'READ' | 'WRITE';

export interface Permissions {
  // =========================================
  // Organization Role Checks
  // =========================================
  
  /**
   * The user's org roles array (raw)
   */
  userOrgRoles: number[];
  
  /**
   * Check if user has a specific org role
   */
  hasOrgRole: (role: OrgRole) => boolean;
  
  /**
   * Convenience flags for common role checks
   */
  isOrgOwner: boolean;       // Has OWNER role (0xff)
  isOrgAdmin: boolean;       // Has ADMINISTRATORS role (0xfe)
  hasWorkspacesRole: boolean; // Has WORKSPACES role (0x02)
  hasBillingRole: boolean;   // Has BILLING role (0x01)
  
  /**
   * Combined checks for common patterns
   */
  isOrgManager: boolean;     // OWNER or ADMINISTRATORS - full org management
  canManageWorkspaces: boolean; // OWNER, ADMINISTRATORS, or WORKSPACES
  
  // =========================================
  // Workspace Role Checks
  // =========================================
  
  /**
   * Get all workspaces the user has access to
   */
  workspaces: Workspace[];
  
  /**
   * Get user's role in a specific workspace
   * Returns undefined if user has no role in the workspace
   */
  getWorkspaceRole: (workspaceId: string) => WorkspaceRole | undefined;
  
  /**
   * Check if user is admin of a specific workspace
   * (or has org-level access that grants admin privileges)
   */
  isWorkspaceAdmin: (workspaceId: string) => boolean;
  
  /**
   * Check if user can access a workspace (has any role)
   */
  canAccessWorkspace: (workspaceId: string) => boolean;
  
  /**
   * Check if user can write to a workspace (ADMIN or WRITE role)
   */
  canWriteToWorkspace: (workspaceId: string) => boolean;
  
  // =========================================
  // Utility Methods
  // =========================================
  
  /**
   * Check if the user has any elevated permissions at all
   * (not just a basic USER)
   */
  hasElevatedPermissions: boolean;
}

/**
 * Hook to access user permissions across the application.
 * 
 * This provides a centralized way to check:
 * - Organization-level roles (OWNER, ADMINISTRATORS, WORKSPACES, BILLING, USER)
 * - Workspace-level roles (ADMIN, WRITE, READ)
 * 
 * @example
 * ```tsx
 * import { usePermissions, OrgRoles } from '../hooks/usePermissions';
 * 
 * function MyComponent() {
 *   const permissions = usePermissions();
 *   
 *   // Check org-level permissions
 *   if (permissions.isOrgOwner) {
 *     // Show owner-only features
 *   }
 *   
 *   if (permissions.canManageWorkspaces) {
 *     // Show workspace management features
 *   }
 *   
 *   // Check workspace-level permissions
 *   const workspaceId = 'workspace-123';
 *   if (permissions.isWorkspaceAdmin(workspaceId)) {
 *     // Show admin features for this workspace
 *   }
 *   
 *   // Check specific org role
 *   if (permissions.hasOrgRole(OrgRoles.BILLING)) {
 *     // Show billing features
 *   }
 * }
 * ```
 */
export const usePermissions = (): Permissions => {
  const { globalData } = useAuth();
  
  const permissions = useMemo<Permissions>(() => {
    const userOrgRoles = globalData.userProfile?.orgRoles || [];
    const workspaces = globalData.workspaces || [];
    
    // Helper to check if user has a specific org role
    const hasOrgRole = (role: OrgRole): boolean => {
      return userOrgRoles.includes(role);
    };
    
    // Organization role flags
    const isOrgOwner = hasOrgRole(OrgRoles.OWNER);
    const isOrgAdmin = hasOrgRole(OrgRoles.ADMINISTRATORS);
    const hasWorkspacesRole = hasOrgRole(OrgRoles.WORKSPACES);
    const hasBillingRole = hasOrgRole(OrgRoles.BILLING);
    
    // Combined permission checks
    const isOrgManager = isOrgOwner || isOrgAdmin;
    const canManageWorkspaces = isOrgOwner || isOrgAdmin || hasWorkspacesRole;
    
    // Workspace role helpers
    const getWorkspaceRole = (workspaceId: string): WorkspaceRole | undefined => {
      const ws = workspaces.find(w => w.id === workspaceId);
      return ws?.userRole;
    };
    
    const isWorkspaceAdmin = (workspaceId: string): boolean => {
      // Org managers have admin access to all workspaces
      if (canManageWorkspaces) return true;
      return getWorkspaceRole(workspaceId) === 'ADMIN';
    };
    
    const canAccessWorkspace = (workspaceId: string): boolean => {
      // Org managers can access all workspaces
      if (canManageWorkspaces) return true;
      // Check if user has any role in this workspace
      return getWorkspaceRole(workspaceId) !== undefined;
    };
    
    const canWriteToWorkspace = (workspaceId: string): boolean => {
      // Org managers have full access
      if (canManageWorkspaces) return true;
      const role = getWorkspaceRole(workspaceId);
      return role === 'ADMIN' || role === 'WRITE';
    };
    
    // Check if user has any elevated permissions
    const hasElevatedPermissions = 
      isOrgOwner || 
      isOrgAdmin || 
      hasWorkspacesRole || 
      hasBillingRole ||
      workspaces.some(w => w.userRole === 'ADMIN');
    
    return {
      // Raw data
      userOrgRoles,
      workspaces,
      
      // Org role checks
      hasOrgRole,
      isOrgOwner,
      isOrgAdmin,
      hasWorkspacesRole,
      hasBillingRole,
      isOrgManager,
      canManageWorkspaces,
      
      // Workspace role checks
      getWorkspaceRole,
      isWorkspaceAdmin,
      canAccessWorkspace,
      canWriteToWorkspace,
      
      // Utility
      hasElevatedPermissions,
    };
  }, [globalData.userProfile?.orgRoles, globalData.workspaces]);
  
  return permissions;
};

export default usePermissions;
